import { User } from "firebase/auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../../hooks/useIsMobile";
import {
  fetchAutomationConfig, updateAutomationConfig, deleteAutomationConfig,
  fetchFplPreview, importFplData, updateClubExternalTeamId,
  AdminCompetition, CompetitionAutomationConfig,
  FplPreviewTeam, FplImportResult,
} from "../../../api/admin";
import { Club } from "../../../api/clubs";
import {
  card, primaryBtn, secondaryBtn,
  selectStyle, tableStyle, thStyle, tdStyle,
  badge, errorStyle, panelHeading,
} from "../../../styles";

interface Props {
  user: User;
  comp: AdminCompetition;
  reload: () => Promise<void>;
  allClubs: Club[];
  automationConfig: CompetitionAutomationConfig | null;
  onAutomationConfigChange: (cfg: CompetitionAutomationConfig | null) => void;
}

export default function AutomationTab({
  user, comp, reload, allClubs, automationConfig, onAutomationConfigChange,
}: Props) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [automationSaving, setAutomationSaving] = useState(false);
  const [automationError, setAutomationError] = useState("");

  const [fplPreview, setFplPreview] = useState<FplPreviewTeam[] | null>(null);
  const [fplPreviewMeta, setFplPreviewMeta] = useState<{ playerCount: number; gameweekCount: number } | null>(null);
  const [fplPreviewLoading, setFplPreviewLoading] = useState(false);
  const [fplPreviewError, setFplPreviewError] = useState("");
  const [fplClubMappings, setFplClubMappings] = useState<Record<number, string>>({});
  const [fplImporting, setFplImporting] = useState(false);
  const [fplImportResult, setFplImportResult] = useState<FplImportResult | null>(null);
  const [fplImportError, setFplImportError] = useState("");

  const [selectedSource, setSelectedSource] = useState<"FPL" | "MANUAL" | "">("");
  const [isChangingSource, setIsChangingSource] = useState(false);

  const [editingExternalIdClubId, setEditingExternalIdClubId] = useState<string | null>(null);
  const [editingExternalIdValue, setEditingExternalIdValue] = useState("");
  const [externalIdError, setExternalIdError] = useState("");

  const saveExternalTeamId = async (clubId: string, value: string) => {
    setEditingExternalIdClubId(null);
    try {
      await updateClubExternalTeamId(user, comp.id, clubId, value.trim() || null);
      await reload();
    } catch {
      setExternalIdError(t("admin.fplImport.errors.externalIdSaveFailed"));
    }
  };

  const handleAutomationToggle = async (field: "enabled" | "autoSubmit", value: boolean) => {
    if (!automationConfig) return;
    setAutomationSaving(true);
    setAutomationError("");
    try {
      const updated = await updateAutomationConfig(user, comp.id, { [field]: value });
      onAutomationConfigChange(updated);
    } catch {
      setAutomationError(t("admin.fplAutomation.errors.failedToSave"));
    } finally {
      setAutomationSaving(false);
    }
  };

  const handleFplPreview = async () => {
    setFplPreviewLoading(true);
    setFplPreviewError("");
    setFplImportResult(null);
    try {
      const data = await fetchFplPreview(user, comp.id);
      setFplPreview(data.teams);
      setFplPreviewMeta({ playerCount: data.playerCount, gameweekCount: data.gameweekCount });
      const initial: Record<number, string> = {};
      data.teams.forEach((t) => { if (t.suggestedClubId) initial[t.externalTeamId] = t.suggestedClubId; });
      setFplClubMappings(initial);
    } catch (err: any) {
      setFplPreviewError(err?.message ?? t("admin.fplImport.errors.previewFailed"));
    } finally {
      setFplPreviewLoading(false);
    }
  };

  const handleFplImport = async () => {
    if (!fplPreview) return;
    const clubMappings = fplPreview
      .map((t) => ({ externalTeamId: t.externalTeamId, clubId: fplClubMappings[t.externalTeamId] ?? "" }))
      .filter((m) => m.clubId);
    if (clubMappings.length === 0) {
      setFplImportError(t("admin.fplImport.errors.noMappings")); return;
    }
    setFplImporting(true);
    setFplImportError("");
    try {
      const result = await importFplData(user, comp.id, clubMappings);
      setFplImportResult(result);
      const cfg = await fetchAutomationConfig(user, comp.id).catch(() => null);
      onAutomationConfigChange(cfg);
      setIsChangingSource(false);
    } catch (err: any) {
      setFplImportError(err?.message ?? t("admin.fplImport.errors.importFailed"));
    } finally {
      setFplImporting(false);
    }
  };

  const isSaving = automationSaving;
  const effectiveSource = isChangingSource ? selectedSource : (automationConfig?.sourceType ?? selectedSource);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

      {/* ── Section 1: Data Source ── */}
      <div style={card}>
        <h2 style={panelHeading}>Data Source</h2>
        {automationConfig && !isChangingSource ? (
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
            <span style={{ ...badge, background: "var(--color-status-active-bg)", color: "var(--color-status-active-text)", fontSize: "0.82rem", padding: "0.2rem 0.6rem" }}>
              Connected: {automationConfig.sourceType === "MANUAL" ? "Manual (manual fixture entry)" : "FPL"}
            </span>
            <button style={secondaryBtn} onClick={() => { setIsChangingSource(true); setSelectedSource(""); setFplPreview(null); setFplImportResult(null); }}>
              Change source
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
            <select
              style={{ ...selectStyle, minWidth: "160px" }}
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value as "FPL" | "MANUAL" | "")}
            >
              <option value="">— None —</option>
              <option value="FPL">FPL {comp.type === "CLUB" ? "(Recommended)" : ""}</option>
              <option value="MANUAL">Manual — manual fixture entry {comp.type === "INTERNATIONAL" ? "(Recommended)" : ""}</option>
            </select>
            {isChangingSource && (
              <button style={primaryBtn} onClick={async () => {
                if (selectedSource === "" && automationConfig && comp) {
                  try {
                    await deleteAutomationConfig(user, comp.id);
                    onAutomationConfigChange(null);
                    setIsChangingSource(false);
                  } catch {
                    setAutomationError("Failed to remove automation config");
                  }
                } else if (selectedSource === "FPL" || selectedSource === "MANUAL") {
                  try {
                    const updated = await updateAutomationConfig(user, comp.id, { sourceType: selectedSource });
                    onAutomationConfigChange(updated);
                    setIsChangingSource(false);
                  } catch {
                    setAutomationError("Failed to save data source");
                  }
                } else {
                  setIsChangingSource(false);
                  setSelectedSource(automationConfig?.sourceType ?? "");
                }
              }}>
                Save
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Section 1.5: Per-club external team id mapping ── */}
      {comp.clubs.length > 0 && (
        <div style={card}>
          <h2 style={panelHeading}>{t("admin.fplImport.externalIds.title")}</h2>
          <p style={{ fontSize: "0.85rem", color: "var(--color-text-muted)", marginTop: 0 }}>
            {t("admin.fplImport.externalIds.description")}
          </p>
          {externalIdError && <p style={errorStyle}>{externalIdError}</p>}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            {comp.clubs.map((club) => (
              <div key={club.id} style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                <span style={{ flex: 1, fontSize: "0.88rem", color: "var(--color-text)" }}>{club.name}</span>
                <input
                  aria-label={t("admin.fplImport.externalIds.inputLabel", { name: club.name })}
                  value={editingExternalIdClubId === club.id ? editingExternalIdValue : (club.externalTeamId ?? "")}
                  placeholder={t("admin.fplImport.externalIds.placeholder")}
                  style={{ fontSize: "0.85rem", padding: "4px 8px", borderRadius: "6px", border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-text)", width: "110px" }}
                  onFocus={() => { setEditingExternalIdClubId(club.id); setEditingExternalIdValue(club.externalTeamId ?? ""); }}
                  onChange={(e) => setEditingExternalIdValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveExternalTeamId(club.id, editingExternalIdValue);
                    else if (e.key === "Escape") setEditingExternalIdClubId(null);
                  }}
                  onBlur={() => { if (editingExternalIdClubId === club.id) saveExternalTeamId(club.id, editingExternalIdValue); }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Section 2: Connection Test & Team Mapping ── */}
      {effectiveSource === "FPL" && (
        <div style={card}>
          <h2 style={panelHeading}>Team Mapping — FPL</h2>
          <p style={{ fontSize: "0.88rem", color: "var(--color-text-muted)", marginTop: 0 }}>
            {t("admin.fplImport.description")}
          </p>
          {fplPreviewError && (
            <p style={{ ...errorStyle, padding: "0.6rem 0.75rem", borderRadius: "8px", marginBottom: "0.75rem" }}>
              {fplPreviewError.includes("not live yet") || fplPreviewError.includes("no events")
                ? "FPL data is unavailable — the season may be in its off-season, or the API may be temporarily unreachable."
                : fplPreviewError}
            </p>
          )}
          {fplImportError && <p style={errorStyle}>{fplImportError}</p>}
          {fplImportResult && (
            <p style={{ fontSize: "0.88rem", color: "var(--color-status-active-text)", background: "var(--color-status-active-bg)", padding: "0.5rem 0.75rem", borderRadius: "8px" }}>
              {t("admin.fplImport.success", { clubs: fplImportResult.clubsLinked, players: fplImportResult.playersSynced, gameweeks: fplImportResult.gameweeksSynced })}
            </p>
          )}
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
            <button style={secondaryBtn} onClick={handleFplPreview} disabled={fplPreviewLoading || fplImporting}>
              {fplPreviewLoading ? t("admin.loading") : t("admin.fplImport.previewBtn")}
            </button>
            {fplPreview && (
              <button style={primaryBtn} onClick={handleFplImport} disabled={fplImporting || fplPreviewLoading}>
                {fplImporting ? t("admin.loading") : t("admin.fplImport.importBtn")}
              </button>
            )}
          </div>
          {fplPreviewLoading && (
            <p style={{ fontSize: "0.83rem", color: "var(--color-text-muted)", margin: "0 0 0.75rem" }}>
              Fetching FPL data — this may take a moment…
            </p>
          )}
          {fplPreview && fplPreviewMeta && (
            <>
              <div style={{ display: "flex", gap: "0.4rem", alignItems: "center", marginBottom: "0.75rem" }}>
                <span style={{ ...badge, background: "var(--color-status-active-bg)", color: "var(--color-status-active-text)", fontSize: "0.8rem" }}>
                  Connected
                </span>
                <span style={{ fontSize: "0.83rem", color: "var(--color-text-muted)" }}>
                  {fplPreview.length} teams · {fplPreviewMeta.playerCount} players · {fplPreviewMeta.gameweekCount} gameweeks
                </span>
              </div>
              <div style={{ overflowX: isMobile ? "auto" : "visible" }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t("admin.fplImport.table.fplName")}</th>
                      <th style={thStyle}>{t("admin.fplImport.table.club")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fplPreview.map((team) => (
                      <tr key={team.externalTeamId}>
                        <td style={tdStyle}>{team.fplName} <span style={{ color: "var(--color-text-muted)", fontSize: "0.8rem" }}>({team.fplShortName})</span></td>
                        <td style={tdStyle}>
                          <select
                            style={{ ...selectStyle, minWidth: isMobile ? "140px" : "200px" }}
                            value={fplClubMappings[team.externalTeamId] ?? ""}
                            onChange={(e) => setFplClubMappings((prev) => ({ ...prev, [team.externalTeamId]: e.target.value }))}
                          >
                            <option value="">{t("admin.fplImport.table.noMatch")}</option>
                            {allClubs.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Section 3: Sync Settings (FPL only — MANUAL is manual fixture entry, nothing to sync) ── */}
      {automationConfig && !isChangingSource && automationConfig.sourceType === "FPL" && (
        <div style={card}>
          <h2 style={panelHeading}>Sync Settings</h2>
          {automationError && <p style={errorStyle}>{automationError}</p>}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.88rem", cursor: isSaving ? "default" : "pointer" }}>
              <input type="checkbox" checked={automationConfig.enabled} disabled={isSaving}
                onChange={(e) => handleAutomationToggle("enabled", e.target.checked)} />
              {t("admin.fplAutomation.enabled")}
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.88rem", cursor: isSaving || !automationConfig.enabled ? "default" : "pointer", opacity: automationConfig.enabled ? 1 : 0.5 }}>
              <input type="checkbox" checked={automationConfig.autoSubmit}
                disabled={isSaving || !automationConfig.enabled}
                onChange={(e) => handleAutomationToggle("autoSubmit", e.target.checked)} />
              {t("admin.fplAutomation.autoSubmit")}
            </label>
            <p style={{ margin: "0 0 0 1.6rem", fontSize: "0.78rem", color: "var(--color-text-muted)" }}>
              {t("admin.fplAutomation.autoSubmitHint")}
            </p>
          </div>
        </div>
      )}


    </div>
  );
}
